import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Brain, Clock, Zap, BarChart3, Palette, Globe, Shield, Database, MessageSquare, Settings, ChevronRight, Sparkles, Star, CheckCircle, TrendingUp, Users, Eye, X, AlertTriangle } from 'lucide-react';
import mockData from '../mock';
import SEOHead from '../components/SEOHead';

const Features = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeFeature, setActiveFeature] = useState(0);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % allFeatures.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const IconComponent = ({ name, className }) => {
    const icons = {
      Brain, Clock, Zap, BarChart3, Palette, Globe, Shield, Database, MessageSquare, Settings, Eye, Users, TrendingUp
    };
    const Icon = icons[name] || Brain;
    return <Icon className={className} />;
  };

  const additionalFeatures = [
    {
      id: 7,
      title: "Reliable Security",
      description: "Your data is protected with standard security measures and best practices for safe operation.",
      icon: "Shield",
      benefits: ["Data encryption", "Secure connections", "Privacy protection"]
    },
    {
      id: 8,
      title: "Smart Data Management",
      description: "We store your data wisely and provide you with valuable statistics and insights about your customer interactions.",
      icon: "Database",
      benefits: ["Intelligent data storage", "Conversation analytics", "Usage statistics", "Performance insights"]
    },
    {
      id: 9,
      title: "Advanced Conversation Management",
      description: "Your chatbot handles conversations intelligently with the ability to understand context and provide relevant responses.",
      icon: "MessageSquare",
      benefits: ["Context understanding", "Conversation flow", "Response relevance", "Query handling"]
    },
    {
      id: 10,
      title: "Analytics Dashboard (coming soon...)",
      description: "Monitor your chatbot's performance with clear insights and reports about customer interactions.",
      icon: "Eye",
      benefits: ["Performance monitoring", "Usage reports", "Interaction insights", "Advanced analytics (coming soon...)"]
    },
    {
      id: 11,
      title: "Team Access",
      description: "Allow your team members to access and monitor the chatbot's performance and conversations.",
      icon: "Users",
      benefits: ["Team access", "Shared monitoring", "User management", "Access controls"]
    },
    {
      id: 12,
      title: "Performance Monitoring",
      description: "Keep track of your chatbot's performance and response quality to ensure optimal operation.",
      icon: "TrendingUp",
      benefits: ["Response monitoring", "Performance tracking", "Quality assurance", "Usage optimization"]
    }
  ];

  const allFeatures = [...mockData.features, ...additionalFeatures];

  const techSpecs = [
    { category: "AI & Machine Learning", items: [
      { name: "Natural Language Processing", level: "Advanced", color: "from-green-400 to-emerald-500" },
      { name: "Context Understanding", level: "Deep Learning", color: "from-blue-400 to-cyan-500" },
      { name: "Response Time", level: "< 1 second", color: "from-violet-400 to-purple-500" },
      { name: "Accuracy Rate", level: "98%+", color: "from-amber-400 to-orange-500" }
    ]},
    { category: "Performance & Scalability", items: [
      { name: "Concurrent Users", level: "Unlimited", color: "from-red-400 to-pink-500" },
      { name: "Uptime Guarantee", level: "99.9%", color: "from-green-400 to-emerald-500" },
      { name: "Data Processing", level: "Real-time", color: "from-blue-400 to-cyan-500" },
      { name: "Global CDN", level: "Worldwide", color: "from-violet-400 to-purple-500" }
    ]},
    { category: "Security & Compliance", items: [
      { name: "Data Encryption", level: "AES-256", color: "from-amber-400 to-orange-500" },
      { name: "GDPR Compliance", level: "Certified", color: "from-red-400 to-pink-500" },
      { name: "SOC 2 Type II", level: "Compliant", color: "from-green-400 to-emerald-500" },
      { name: "Regular Audits", level: "Monthly", color: "from-blue-400 to-cyan-500" }
    ]}
  ];

  return (
    <div className="pt-16 bg-gray-950 overflow-hidden">
      <SEOHead 
        title="Features - BrixBot AI Customer Service" 
        description="Discover BrixBot's advanced AI features for customer service automation. Natural language processing, real-time responses, and intelligent chatbot technology."
        isIndexable={true}
      />
      {/* Hero Section with Parallax */}
      <section className="relative py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-violet-950/30"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        ></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-violet-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 hover:bg-violet-600/30 mb-8 text-lg py-2 px-6">
            <Sparkles className="w-5 h-5 mr-2" />
            Advanced AI Features
          </Badge>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
            Everything You Need for
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-400 animate-pulse">
              Intelligent Customer Service
            </span>
          </h1>
          
          <p className="text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed">
            BrixBot comes packed with advanced features designed to enhance your customer experience and streamline your support operations.
          </p>
          
          <Link to="/demo">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white px-10 py-6 text-xl rounded-3xl shadow-2xl shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300 transform hover:scale-105"
            >
              <Eye className="w-6 h-6 mr-3" />
              See Features in Action
              <ArrowRight className="ml-3 w-6 h-6" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Enhanced Features Grid */}
      <section className="py-32 bg-gray-900/50 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-violet-500/5 to-purple-500/5"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Comprehensive Feature Suite
            </h2>
            <p className="text-xl text-gray-300">
              Everything you need to build, deploy, and optimize your AI chatbot
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allFeatures.map((feature, index) => (
              <Card 
                key={feature.id} 
                className={`bg-gray-900/50 border-violet-500/20 hover:bg-gray-900/70 hover:border-violet-400/40 transition-all duration-500 group backdrop-blur-sm relative overflow-hidden ${
                  activeFeature === index ? 'ring-2 ring-violet-400/50 shadow-2xl shadow-violet-500/20' : ''
                }`}
                style={{ 
                  animationDelay: `${index * 100}ms`,
                  transform: `translateY(${Math.sin(scrollY * 0.01 + index) * 10}px)` 
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <CardHeader className="space-y-4 relative z-10">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-violet-500/25">
                      <IconComponent name={feature.icon} className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute -inset-2 bg-gradient-to-r from-violet-500 to-purple-600 rounded-3xl blur opacity-20 group-hover:opacity-40 transition-opacity duration-300"></div>
                  </div>
                  <CardTitle className="text-white text-xl font-bold">{feature.title}</CardTitle>
                </CardHeader>
                
                <CardContent className="space-y-4 relative z-10">
                  <CardDescription className="text-gray-300 text-base leading-relaxed">
                    {feature.description}
                  </CardDescription>
                  
                  <div className="space-y-2">
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-violet-400" />
                        <span className="text-gray-400 text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* AI Model Choice Section */}
      <section className="py-32 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 mb-6">
              <Brain className="w-4 h-4 mr-2" />
              AI Model Selection
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Choose Your AI Model
            </h2>
            <p className="text-xl text-gray-300">
              Select from leading AI models to power your chatbot
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                name: "ChatGPT",
                provider: "OpenAI",
                description: "Advanced conversational AI with excellent natural language understanding",
                color: "from-green-400 to-emerald-500",
                icon: "💬"
              },
              {
                name: "Gemini",
                provider: "Google",
                description: "Powerful multimodal AI model with superior reasoning capabilities",
                color: "from-blue-400 to-cyan-500",
                icon: "✨"
              },
              {
                name: "Claude",
                provider: "Anthropic",
                description: "Helpful, harmless, and honest AI assistant with strong analytical skills",
                color: "from-violet-400 to-purple-500",
                icon: "🧠"
              },
              {
                name: "Custom Model",
                provider: "Your Choice",
                description: "We can integrate other AI models based on your specific requirements",
                color: "from-amber-400 to-orange-500",
                icon: "⚙️"
              }
            ].map((model, index) => (
              <Card key={index} className="bg-gray-900/50 border-violet-500/20 hover:bg-gray-900/70 hover:border-violet-400/40 transition-all duration-500 group backdrop-blur-sm relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <CardHeader className="text-center relative z-10">
                  <div className="text-4xl mb-4">{model.icon}</div>
                  <CardTitle className="text-white text-xl font-bold mb-2">{model.name}</CardTitle>
                  <Badge className={`bg-gradient-to-r ${model.color} text-white border-0 mb-4`}>
                    {model.provider}
                  </Badge>
                </CardHeader>
                
                <CardContent className="relative z-10 text-center">
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {model.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-16">
            <p className="text-lg text-gray-300 mb-8">
              Not sure which model to choose? We'll help you select the best AI model for your specific use case during setup.
            </p>
            <Link to="/contact">
              <Button 
                variant="outline" 
                size="lg"
                className="border-violet-500/50 text-violet-300 hover:bg-violet-600/10 hover:border-violet-400 px-10 py-4 text-lg rounded-2xl backdrop-blur-sm transition-all duration-300"
              >
                <Brain className="w-5 h-5 mr-3" />
                Get AI Model Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="py-32 bg-gray-900/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Why BrixBot Stands Out
            </h2>
            <p className="text-xl text-gray-300">
              Compare our advanced features with traditional solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-gray-900/50 border-red-500/20 backdrop-blur-sm">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <X className="w-8 h-8 text-red-400" />
                </div>
                <CardTitle className="text-white">Traditional Support</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center space-x-2">
                  <X className="w-4 h-4 text-red-400" />
                  <span className="text-gray-400">Limited availability</span>
                </div>
                <div className="flex items-center space-x-2">
                  <X className="w-4 h-4 text-red-400" />
                  <span className="text-gray-400">Slow response times</span>
                </div>
                <div className="flex items-center space-x-2">
                  <X className="w-4 h-4 text-red-400" />
                  <span className="text-gray-400">Inconsistent answers</span>
                </div>
                <div className="flex items-center space-x-2">
                  <X className="w-4 h-4 text-red-400" />
                  <span className="text-gray-400">High operational costs</span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-amber-500/20 backdrop-blur-sm">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-amber-400" />
                </div>
                <CardTitle className="text-white">Basic Chatbots</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <span className="text-gray-400">Limited understanding</span>
                </div>
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <span className="text-gray-400">No personalization</span>
                </div>
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <span className="text-gray-400">Rigid responses</span>
                </div>
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <span className="text-gray-400">Complex setup</span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm ring-2 ring-violet-400/30">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-white">BrixBot AI</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-violet-400" />
                  <span className="text-gray-300">24/7 intelligent support</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-violet-400" />
                  <span className="text-gray-300">Instant responses</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-violet-400" />
                  <span className="text-gray-300">Personalized interactions</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-violet-400" />
                  <span className="text-gray-300">Cost-effective solution</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-r from-violet-900/30 to-purple-900/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 mb-8 text-lg py-2 px-6">
            <Sparkles className="w-5 h-5 mr-2" />
            Ready to Transform?
          </Badge>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Experience
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400">
              These Features?
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
            See how BrixBot can transform your customer service with a demo.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/demo">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white px-10 py-6 text-xl rounded-3xl shadow-2xl shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300 transform hover:scale-105"
              >
                <Eye className="w-6 h-6 mr-3" />
                Check out Demo
              </Button>
            </Link>
            
            <Link to="/pricing">
              <Button 
                variant="outline" 
                size="lg" 
                className="border-violet-500/50 text-violet-300 hover:bg-violet-600/10 hover:border-violet-400 px-10 py-6 text-xl rounded-3xl backdrop-blur-sm transition-all duration-300"
              >
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features;